import { Form } from 'factorial-form'
import { autorun } from 'mobx'

import asyncAction from 'modules/core/lib/asyncAction'

import type { Data } from 'modules/automations/components/Show/CommonNotification/utils'
import {
  accessGroupToRecipient,
  accessGroupToRecipientType,
  buildAccessGroupPayload,
} from 'modules/automations/components/Show/CommonNotification/utils'
import { Recipients } from 'modules/automations/components/Show/CommonNotification/utils'
import { accessGroups } from 'modules/automations/store/collections/accessGroups'
import type { Action } from 'modules/automations/store/models/Action'

import type { Storeable } from './Storeable'

export class SendNotification implements Storeable {
  #action: Action
  disposer: () => void
  form: Form

  constructor(action: Action) {
    this.#action = action
    this.form = new Form(
      {},
      {
        automations_action_id: 'number',
        recipient_type: 'string',
        recipient: 'string',
        notifiable_id: 'number',
        notifiable_ids: 'any', // Form doesn't support array types
      }
    )
    this.disposer = autorun(() => this._updateForm())
  }

  dispose() {
    this.disposer()
  }

  _updateForm() {
    const accessGroup = this.#action.accessGroup

    if (!accessGroup) {
      return this.form.setValues({
        automations_action_id: this.#action.attributes.id,
      })
    }

    const recipientType = accessGroupToRecipientType(accessGroup)
    const recipient = accessGroupToRecipient(accessGroup)

    const notifiableIds = !!(
      recipient === Recipients.access &&
      !accessGroup.attributes.notifiable_ids?.length &&
      accessGroup.attributes.notifiable_id
    )
      ? [accessGroup.attributes.notifiable_id]
      : accessGroup.attributes.notifiable_ids

    return this.form.setValues({
      automations_action_id: this.#action.attributes.id,
      notifiable_ids: notifiableIds,
      recipient_type: recipientType,
      recipient: recipient,
      notifiable_id: accessGroup.attributes.notifiable_id,
    })
  }

  async _saveAccessGroup() {
    const accessGroup = this.#action.accessGroup
    // TODO: we should be able to type `Form` to avoid this dangerous
    //       type conversion.
    const payload = buildAccessGroupPayload(this.form.data() as Data)

    return accessGroup ? accessGroup.save(payload) : accessGroups.create(payload)
  }

  // FIXME: set errors and do not show a success flash
  async save() {
    if (!this.form.isDirty || !this.form.get('recipient').value) {
      return
    }

    await asyncAction(() => this._saveAccessGroup())
  }
}
